'use client'

import { useMemo } from 'react'

type Props = {
  values: number[]
  /** CSS color */
  stroke?: string
  width?: number
  height?: number
  className?: string
}

/** Tiny sparkline for KPI cards — flat when &lt; 2 points. */
export function SparklineMicro({
  values,
  stroke = 'var(--sigma-muted, #6b7280)',
  width = 120,
  height = 22,
  className,
}: Props) {
  const d = useMemo(() => {
    if (values.length < 2) return ''
    const ys = values.map((v) => v)
    const minY = Math.min(...ys)
    const maxY = Math.max(...ys)
    const pad = maxY === minY ? 1 : (maxY - minY) * 0.15
    const y0 = minY - pad
    const y1 = maxY + pad
    const insetY = 2
    const h = height - insetY * 2
    const n = values.length - 1
    return values
      .map((v, i) => {
        const px = (i / n) * width
        const py = y1 === y0 ? height / 2 : insetY + h * (1 - (v - y0) / (y1 - y0))
        return `${i === 0 ? 'M' : 'L'} ${px.toFixed(1)} ${py.toFixed(1)}`
      })
      .join(' ')
  }, [values, height, width])

  if (values.length < 2) return null

  return (
    <svg
      className={className}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      aria-hidden
    >
      <path
        d={d}
        fill="none"
        stroke={stroke}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}
